import { MdOutlineArrowOutward } from "react-icons/md";
import MainLayout from "../components/Layout/MainLayout";
import { FaHeart } from "react-icons/fa";
import { Button, Card, Flex } from "antd";
import Cards from "../common/Card";
import Slide from "../common/Slide";
import BarChart from "../components/Charts/Bar";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { postApi } from "../services/api";
import PrimaryButton from "../components/Forms/PrimaryButton";
import { formatDate, handleRouteNav } from "../utils";
import { useSelector } from "react-redux";
import { roleIds, roleNames } from "../constant";
import moment from "moment";
import { PiBriefcaseFill } from "react-icons/pi";
import { RiDiscountPercentFill } from "react-icons/ri";

const Dashboard = () => {
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState(0);
  const [dashboardData, setDashboardData] = useState();
  const { is_sales_person, is_admin, user_id, role_id } = useSelector((state) => state.auth);
  const [period, setPeriod] = useState(is_admin ? "active" : "daily");

  const [chartData, setChartData] = useState({
    labels: [],
    values: [],
    totalOrders: 0,
  });

  const fetchChartData = async (filter) => {
    try {
      const response = await postApi(is_admin ? "sales/admin-dashboard" : "sales/sales-summary", {
        filter: is_admin ? undefined : filter,
        status: is_admin ? filter : undefined,
        assigned_user_id: is_sales_person ? user_id : undefined,
      });
      if (!response?.status) return;

      if (is_admin) {
        const seq = {
          sales_person: roleIds.SalesPerson,
          sales_manager: roleIds.SalesManager,
          customers: roleIds.Customer,
          admin: roleIds.Admin,
        };
        const totalCount = Object.values(response?.data || {}).reduce((acc, val) => acc + val, 0);
        const labels = Object.values(seq).map((id) => roleNames[id]);
        const values = Object.keys(seq).map((id) => response?.data?.[id] || 0);
        setChartData({ labels, values, totalOrders: totalCount });
        return;
      }

      const now = moment();
      const monthName = now.format("MMM");

      // Clone current date and get the first day of the current month
      const firstDayOfMonth = now.clone().startOf("month");

      // Get offset based on which day of the week the month starts (Monday=0)
      const offset = firstDayOfMonth.isoWeekday() - 1;

      // Calculate current week of the month (rounded up)
      const weekOfMonth = Math.ceil((now.date() + offset) / 7);

      const dateNow = new Date();

      // Helpers to get current period key
      const periodMap = {
        daily: () => formatDate(now, { format: "YYYY-MM-DD" }),
        weekly: () => {
          const weekData = response.data.find((item) => {
            return Number(item.period[item.period.length - 1]) === weekOfMonth;
          });
          return weekData?.period || null;
        },
        monthly: () =>
          `${dateNow.getFullYear()}-${String(dateNow.getMonth() + 1).padStart(2, "0")}`,
        yearly: () => String(dateNow.getFullYear()),
      };

      let totalOrders = response.total_orders;

      if (filter in periodMap) {
        const currentPeriod = periodMap[filter]();
        if (filter === "weekly") {
          totalOrders =
            response.data.find((item) => item.period === currentPeriod)?.order_count || 0;
        } else {
          totalOrders =
            response.data.find((item) => item.period === currentPeriod)?.order_count || 0;
        }
      }

      setChartData({
        labels: response.data.map((item) =>
          filter === "weekly"
            ? monthName + " " + item.label.split(" ").slice(1).join(" ")
            : item.label,
        ),
        values: response.data.map((item) => item.order_count),
        totalOrders,
      });
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  // 🔹 Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const response = await postApi("sales/total-dashboard", {
        user_id: is_sales_person ? user_id : undefined,
      });
      if (response?.status) {
        setDashboardData(response);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  // 🔹 Fetch wishlist count
  const fetchWishlistCount = async () => {
    try {
      const response = await postApi("sales/wishlist/get-wishlist", {});
      setTotalCount(response?.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Effects
  useEffect(() => {
    fetchChartData(period);
  }, [period]);

  useEffect(() => {
    fetchDashboardData();
    fetchWishlistCount();
  }, []);

  // 🔹 Memoized arrays
  const cardArray = useMemo(
    () => [
      {
        title: "Pending Users",
        image: "../../assets/icons/User.png",
        count: dashboardData?.pending_client_count || "0",
        roles: [roleIds.Admin],
      },
      {
        title: "Pending Requests",
        image: "../../assets/icons/watch.png",
        count: dashboardData?.modification_request_count || "0",
      },
      {
        title: "Pending Orders",
        image: "../../assets/icons/twogear.png",
        count:
          dashboardData?.pending_order_count +
            dashboardData?.inproduction_order_count +
            dashboardData?.part_dispatched_order_count || "0",
      },
      {
        title: "Recently Dispatched",
        image: "../../assets/icons/TruckTrailer.png",
        count: dashboardData?.dispatched_order_count || "0",
      },
      {
        title: "Active Schemes",
        image: "../../assets/icons/discount.png",
        count: dashboardData?.offer_count || "0",
        roles: [roleIds.SalesManager, roleIds.SalesPerson],
      },
    ],
    [dashboardData],
  );

  const sliderArray = useMemo(
    () => [
      {
        title: "Total number of Orders placed till date",
        icon: "../../assets/icons/cube.png",
        count: dashboardData?.total_order || "0",
      },
      {
        title: "Total number of Products in catalogue",
        icon: "../../assets/icons/copy.png",
        count: dashboardData?.product_count || "0",
      },
      {
        title: "Total number of assigned Active Clients",
        icon: "../../assets/icons/users.png",
        count: dashboardData?.assigned_client_count || "0",
      },
      {
        title: "Total number of Sales Employees",
        icon: <PiBriefcaseFill size={32} fill="#A33434" />,
        count: dashboardData?.sales_emp_count || "0",
        roles: [roleIds.SalesManager, roleIds.Admin],
      },
      {
        title: "Total number of Pending Clients",
        icon: "../../assets/icons/User.png",
        count: dashboardData?.pending_client_count || "0",
        roles: [roleIds.SalesPerson],
      },
      {
        title: "Total number of Active Discount Schemes",
        icon: <RiDiscountPercentFill color="#A33434" size={30} />,
        count: dashboardData?.offer_count || "0",
        roles: [roleIds.Admin],
      },
    ],
    [dashboardData],
  );

  return (
    <>
      <div className="pb-5 flex flex-col">
        <Flex
          justify="space-between"
          className={`mt-4 flex-col lg:flex-row items-stretch ${is_sales_person && "order-2"}`}
        >
          {/* Left Section */}
          <Flex className="w-full lg:w-[70%] self-stretch justify-between" vertical gap={18}>
            {/* Pending Clients */}
            <div
              className={`bg-white p-6 md:p-7 rounded-3xl border border-[#D9D9D9] ${
                is_sales_person && "order-3"
              }`}
            >
              <div className="flex flex-col md:flex-row gap-5 justify-between items-center">
                {is_sales_person ? (
                  <p className="text-black leading-7 text-base text-start">
                    <strong>{dashboardData?.offter_customer_count || "0"} clients</strong> have been
                    assigned to active discount schemes.{" "}
                    {dashboardData?.offter_customer_count > 0
                      ? "Contact them to boost your sales."
                      : ""}
                  </p>
                ) : (
                  <p className="text-black leading-7 text-base text-start">
                    <strong>{dashboardData?.pending_client_count || "0"} pending clients</strong>{" "}
                    are waiting for your approval.
                  </p>
                )}
                <div className="w-full md:w-[30%] flex justify-center md:justify-center">
                  <PrimaryButton
                    text="Review"
                    onClick={() =>
                      navigate(
                        is_sales_person
                          ? "/sales/discount-schemes"
                          : handleRouteNav("/pending-clients", role_id),
                      )
                    }
                    className="me-[30px]"
                  />
                </div>
              </div>
            </div>

            {/* Bar Chart */}
            <Card
              className="p-2 rounded-3xl h-full"
              classNames={{ body: "flex flex-col h-full gap-y-[auto]" }}
            >
              <BarChart
                period={period}
                onPeriodChange={setPeriod}
                labels={chartData.labels}
                values={chartData.values}
                totalOrders={chartData.totalOrders}
              />
            </Card>

            {/* Slider */}
            <div className="bg-white p-6 md:p-7 rounded-3xl border border-[#D9D9D9]">
              <Slide
                sliderArray={sliderArray.filter((item) =>
                  item.roles?.length ? item.roles.includes(role_id) : true,
                )}
              />
            </div>
          </Flex>

          {/* Right Section */}
          <Flex
            className="w-full lg:w-[28%] mt-6 lg:mt-0 self-stretch min-h-0 overflow-auto"
            vertical
            gap={16}
          >
            {cardArray
              .filter((item) => (item.roles?.length ? item.roles.includes(role_id) : true))
              .map((item, i) => (
                <Cards key={i} item={item} />
              ))}
          </Flex>
        </Flex>

        {/* Favorites */}
        <div className={`bg-white mt-5 p-6 rounded-3xl border border-[#D9D9D9]`}>
          <div className="flex flex-col md:flex-row gap-5 justify-between items-center">
            <div className="w-full md:w-[64%] flex items-center gap-6">
              <Button
                className="rounded-full text-2xl py-6 px-2 w-28 flex-shrink-0"
                onClick={() => navigate(handleRouteNav("/product-catalog/favorites", role_id))}
              >
                <FaHeart className="text-[#A33434] text-2xl" /> {totalCount}
              </Button>
              <p className="text-black leading-7 text-base text-start">
                Your clients may have added to their favorites. Check details and{" "}
                <strong>push for orders.</strong>
              </p>
            </div>
            <div className="w-full md:w-[25%] flex justify-center md:justify-center mr-0 md:mr-6 mt-4 md:mt-0">
              <PrimaryButton
                text="View Favorites"
                onClick={() => navigate(handleRouteNav("/product-catalog/favorites", role_id))}
                className="me-[30px]"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
